import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import toast from 'react-hot-toast';
import { calendarAPI } from '../api/client';
import { isNativePlatform } from '../native';
import type { CalendarProvider } from '../types';
import { parseCalendarCallback } from './calendarNative';
import { closeLinkedInBrowser, openLinkedInInSystemBrowser } from './linkedinNative';

const OAUTH_TIMEOUT_MS = 105_000;
const PENDING_STORAGE_KEY = 'calendar_oauth_pending';

interface PendingOAuth {
  provider: CalendarProvider;
  state: string;
  redirectUri: string;
  expiresAt: number;
}

function readPending(): PendingOAuth | null {
  try {
    const value = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!value) return null;
    const pending = JSON.parse(value) as PendingOAuth;
    if (!pending.provider || !pending.state || !pending.redirectUri || !pending.expiresAt) {
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

export function useCalendarConnect(onConnected: () => void | Promise<void>) {
  const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null);
  const [calendarUnavailable, setCalendarUnavailable] = useState<
    Partial<Record<CalendarProvider, boolean>>
  >({});
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const pendingRef = useRef<PendingOAuth | null>(null);
  const callbackSeenRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const popupPollRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);
  const webHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const clearTimersAndPopup = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    if (popupPollRef.current) window.clearInterval(popupPollRef.current);
    timeoutRef.current = null;
    popupPollRef.current = null;
    if (webHandlerRef.current) {
      window.removeEventListener('message', webHandlerRef.current);
      webHandlerRef.current = null;
    }
    popupRef.current?.close();
    popupRef.current = null;
  }, []);

  const clearPending = useCallback(() => {
    const pending = pendingRef.current;
    if (pending && readPending()?.state === pending.state) {
      localStorage.removeItem(PENDING_STORAGE_KEY);
    }
    pendingRef.current = null;
    callbackSeenRef.current = false;
  }, []);

  const finish = useCallback((
    nextProvider: CalendarProvider | null = null,
    message?: { ok?: string; error?: string },
  ) => {
    clearTimersAndPopup();
    void closeLinkedInBrowser();
    clearPending();
    setConnectingProvider(nextProvider);
    if (message?.ok) toast.success(message.ok);
    if (message?.error) toast.error(message.error);
  }, [clearPending, clearTimersAndPopup]);

  const exchangePending = useCallback(async (code: string, pending: PendingOAuth) => {
    try {
      await calendarAPI.exchange(pending.provider, code, pending.redirectUri);
      await onConnectedRef.current();
      finish(null, { ok: 'Calendar connected. Your Forum events will sync shortly.' });
    } catch {
      finish(null, { error: 'Could not connect your calendar. Please try again.' });
    }
  }, [finish]);

  const handleNativeUrl = useCallback((rawUrl: string) => {
    const callback = parseCalendarCallback(rawUrl);
    if (!callback) return;
    const pending = pendingRef.current || readPending();
    if (!pending || callback.state !== pending.state) return;
    if (callbackSeenRef.current) return;
    pendingRef.current = pending;
    callbackSeenRef.current = true;
    void closeLinkedInBrowser();
    if (callback.error || !callback.code) {
      finish(null, { error: 'Could not connect your calendar. Please try again.' });
      return;
    }
    void exchangePending(callback.code, pending);
  }, [exchangePending, finish]);

  useEffect(() => {
    if (!isNativePlatform) {
      return () => clearTimersAndPopup();
    }

    let disposed = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    void Promise.all([
      App.addListener('appUrlOpen', ({ url }) => handleNativeUrl(url)),
      Browser.addListener('browserFinished', () => {
        if (callbackSeenRef.current || !pendingRef.current) return;
        finish(null, { error: 'Calendar connection was cancelled.' });
      }),
    ]).then((registered) => {
      if (disposed) {
        registered.forEach((handle) => void handle.remove());
      } else {
        handles.push(...registered);
      }
    });

    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) handleNativeUrl(launch.url);
    });

    return () => {
      disposed = true;
      clearTimersAndPopup();
      handles.forEach((handle) => void handle.remove());
    };
  }, [clearTimersAndPopup, finish, handleNativeUrl]);

  const connectCalendar = useCallback(async (provider: CalendarProvider) => {
    clearTimersAndPopup();
    setConnectingProvider(provider);
    callbackSeenRef.current = false;
    const popup = isNativePlatform
      ? null
      : window.open('', 'calendar-oauth', 'width=600,height=720');
    if (!isNativePlatform && !popup) {
      finish(null, {
        error: 'Your browser blocked the calendar window. Allow popups and try again.',
      });
      return;
    }
    popupRef.current = popup;
    try {
      const res = await calendarAPI.getAuthUrl(provider);
      if (!res.configured || !res.authorization_url || !res.state || !res.redirect_uri) {
        setCalendarUnavailable((previous) => ({ ...previous, [provider]: true }));
        finish(null);
        return;
      }

      const pending: PendingOAuth = {
        provider,
        state: res.state,
        redirectUri: res.redirect_uri,
        expiresAt: Date.now() + OAUTH_TIMEOUT_MS,
      };
      pendingRef.current = pending;
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        finish(null, { error: 'Calendar connect did not finish in time. Please try again.' });
      }, OAUTH_TIMEOUT_MS);

      if (isNativePlatform) {
        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending));
        void openLinkedInInSystemBrowser(res.authorization_url);
        return;
      }

      if (!popup || popup.closed) {
        finish(null, { error: 'Calendar connection was cancelled.' });
        return;
      }
      popup.location.assign(res.authorization_url);

      const handler = (event: MessageEvent) => {
        if (
          event.origin !== window.location.origin
          || event.source !== popup
          || !event.data
          || event.data.type !== 'calendar-oauth'
        ) {
          return;
        }
        callbackSeenRef.current = true;
        if (event.data.error || !event.data.code) {
          finish(null, { error: 'Could not connect your calendar. Please try again.' });
          return;
        }
        void exchangePending(event.data.code as string, pending);
      };
      webHandlerRef.current = handler;
      window.addEventListener('message', handler);
      popupPollRef.current = window.setInterval(() => {
        if (popup.closed && !callbackSeenRef.current) {
          finish(null, { error: 'Calendar connection was cancelled.' });
        }
      }, 500);
    } catch {
      setCalendarUnavailable((previous) => ({ ...previous, [provider]: true }));
      finish(null);
    }
  }, [clearTimersAndPopup, exchangePending, finish]);

  return {
    connectCalendar,
    connectingProvider,
    calendarUnavailable,
  };
}
